"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import CloseIcon from "@mui/icons-material/Close";
import ScoreCard from "./ScoreCard/ScoreCard";
import { TournamentLeaderboard } from "../queries/tournament-leaderboard";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { GOLF_PORTAL_API } from "@/utils/constants";
import clsx from "clsx";

interface LeaderboardModalProps {
  tournamentId: string;
  pageSlug: string;
  isMultiRoundTournament: boolean;
}

const modalVariants = {
  initial: {
    y: "100%",
    transition: {
      duration: 0.3,
    },
  },
  open: {
    y: 0,
    transition: {
      duration: 0.3,
      ease: "linear",
    },
  },
};

const fadeInVariants = {
  initial: {
    opacity: 0,
    transition: {
      duration: 0.3,
      delay: 0.2,
    },
  },
  open: {
    opacity: 1,
  },
};

const LeaderboardModal = ({
  tournamentId,
  pageSlug,
  isMultiRoundTournament,
}: LeaderboardModalProps) => {
  const searchParams = useSearchParams();
  const playerid = searchParams.get("playerid");
  const pathname = usePathname();
  const { replace: routerReplace } = useRouter();
  const basePath = pathname?.split("?")[0];

  const [isModalOpen, setIsModalOpen] = useState(!!playerid);
  const [hasScorecardLoaded, setHasScorecardLoaded] = useState(false);

  useEffect(() => {
    if (typeof document !== "undefined") {
      if (playerid && isModalOpen && pageSlug === "leaderboard") {
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.width = "100%";
      } else {
        document.body.style.overflow = "auto";
        document.body.style.position = "static";
        document.body.style.width = "unset";
      }
    }
  }, [playerid, pageSlug, isModalOpen]);

  const { data, isLoading: isLeaderboardDataLoading } = useQuery({
    queryKey: ["tournament_leaderboard_share_scorecard"],
    queryFn: () => {
      return axios.post(GOLF_PORTAL_API, {
        query: TournamentLeaderboard,
        variables: {
          tournamentId,
          playerIds: [playerid || ""],
          gender: null,
          take: 0,
        },
      });
    },
    enabled: !!playerid,
  });

  const player = data?.data.data.node.leaderboard.selectedPlayers[0];

  if (!player || !isModalOpen) return null;

  return (
    <motion.div
      variants={fadeInVariants}
      className={clsx(
        `fixed top-0 left-0 z-[5] flex h-screen w-full flex-col justify-end bg-[rgba(0,0,0,0.6)] backdrop-blur-[10px]`,
        {
          "pointer-events-auto": isModalOpen,
          "pointer-events-none": !isModalOpen,
        }
      )}
      animate="open"
      initial="initial"
      exit="initial"
    >
      <div className="grid-container my-0 mx-auto w-full">
        <div className="col-start-1 col-span-4 lg:col-start-2 lg:col-span-14 2xl:col-start-6 2xl:col-span-22">
          <motion.div
            animate={!isLeaderboardDataLoading ? "open" : "initial"}
            initial="initial"
            exit="initial"
            variants={modalVariants}
            className="relative overflow-auto rounded-t-[10px] bg-tm-c-gray-950 2xl:pb-15"
          >
            <div className="pt-5 px-5 pb-0 text-right">
              <CloseIcon
                className="block my-0 mr-0 ml-auto cursor-pointer transition-opacity duration-300 hover:opacity-60"
                style={{
                  fontSize: 26,
                }}
                onClick={() => {
                  setIsModalOpen(false);
                  routerReplace(basePath);
                }}
              />
            </div>
            <div className="grid [&>*]:row-start-1 [&>*]:col-start-1">
              {!hasScorecardLoaded && (
                <div className="flex flex-col gap-[30px] w-full py-10 px-5">
                  <div className="m-0 lg:my-0 lg:mx-5 w-full lg:w-[calc(100%-40px)] h-[100px] bg-tm-c-dark rounded-[10px] opacity-60" />
                  <div className="m-0 lg:my-0 lg:mx-5 w-full lg:w-[calc(100%-40px)] h-[180px] bg-tm-c-dark rounded-[10px] opacity-60" />
                  <div className="m-0 lg:my-0 lg:mx-5 w-full h-[580px] bg-tm-c-dark rounded-[10px] opacity-60" />
                </div>
              )}
              <motion.div
                variants={fadeInVariants}
                animate={hasScorecardLoaded ? "open" : "initial"}
                initial="initial"
              >
                <ScoreCard
                  multiRoundScoreCardIds={player.rounds.map(
                    ({
                      roundNumber,
                      scorecardId,
                    }: {
                      roundNumber: number;
                      scorecardId: string;
                    }) => ({
                      roundNumber,
                      scorecardId,
                    })
                  )}
                  fetchData
                  isOpen
                  playerMuted
                  showVideoPlaceholder
                  playerId={player.playerId}
                  playerScoreCardId={player.latestRound.scorecardId}
                  shouldIntervalRefetch={player.total.thru < 18}
                  profilePicture={player.picture}
                  playerName={player.playername}
                  playerHcp={player.hcp}
                  playerGender={player.gender}
                  setHasScorecardLoaded={setHasScorecardLoaded}
                  isMultiRoundTournament={isMultiRoundTournament}
                  latestRound={player.latestRound?.roundNumber}
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
};

export default LeaderboardModal;
